import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { Search, FileText, User, Eye, Clock, CheckCircle2, Calendar, XCircle, Linkedin } from 'lucide-react';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [filter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      
      const response = await axios.get(`${API}/admin/applications?${params.toString()}`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (appId, newStatus, notes = null) => {
    setUpdating(true);
    try {
      const params = new URLSearchParams();
      if (newStatus) params.append('status', newStatus);
      if (notes !== null) params.append('admin_notes', notes);
      
      await axios.put(`${API}/admin/applications/${appId}?${params.toString()}`);
      toast.success('Application updated');
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error('Failed to update application');
    } finally {
      setUpdating(false);
    }
  };

  const filteredApps = applications.filter(a => {
    if (search) {
      const searchLower = search.toLowerCase();
      return a.talent_name?.toLowerCase().includes(searchLower) || 
             a.project_title?.toLowerCase().includes(searchLower) ||
             a.company_name?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  const statusColors = {
    submitted: 'badge-neutral',
    under_review: 'badge-warning',
    shortlisted: 'badge-primary',
    intro_scheduled: 'badge-success',
    not_selected: 'badge-error',
  };

  return (
    <AdminLayout>
      <div data-testid="admin-applications-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">Manage Applications</h1>
            <p className="text-slate-600">{filteredApps.length} applications</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6" data-testid="application-filters">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by talent, project, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="shortlisted">Shortlisted</SelectItem>
                <SelectItem value="intro_scheduled">Intro Scheduled</SelectItem>
                <SelectItem value="not_selected">Not Selected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Applications Table */}
        <Card className="overflow-hidden" data-testid="applications-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Talent</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Project</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Applied</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={6} className="px-6 py-4">
                        <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
                      </td>
                    </tr>
                  ))
                ) : filteredApps.length > 0 ? (
                  filteredApps.map((app) => (
                    <tr key={app.id} className="hover:bg-slate-50" data-testid={`application-row-${app.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{app.talent_name}</p>
                            <p className="text-sm text-slate-500">{app.talent_function}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-900">{app.project_title}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{app.company_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`${statusColors[app.status]} text-xs`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedApp(app);
                            setAdminNotes(app.admin_notes || '');
                          }}
                          data-testid={`view-app-${app.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      No applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="application-detail-modal">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedApp.talent_name}</h3>
                  <p className="text-sm text-slate-500">{selectedApp.talent_headline || selectedApp.talent_function}</p>
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Applied for</p>
                <p className="font-medium text-slate-900">{selectedApp.project_title}</p>
                <p className="text-sm text-slate-500">{selectedApp.company_name}</p>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-1">Status</p>
                <span className={`${statusColors[selectedApp.status]}`}>
                  {selectedApp.status.replace('_', ' ')}
                </span>
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Pitch Note</p>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedApp.pitch_note}</p>
                </div>
              </div>

              <div className="flex gap-4">
                {selectedApp.talent_linkedin && (
                  <a href={selectedApp.talent_linkedin} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Linkedin className="h-4 w-4 mr-2" />
                      LinkedIn
                    </Button>
                  </a>
                )}
                {selectedApp.talent_resume && (
                  <a href={selectedApp.talent_resume} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <FileText className="h-4 w-4 mr-2" />
                      Resume
                    </Button>
                  </a>
                )}
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Admin Notes</p>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes..."
                  className="mb-3"
                />
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-3">Update Status</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => updateApplicationStatus(selectedApp.id, 'under_review', adminNotes)}
                    disabled={updating}
                    className="text-sm"
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Under Review
                  </Button>
                  <Button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'shortlisted', adminNotes)}
                    disabled={updating}
                    className="btn-primary text-sm"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Shortlist
                  </Button>
                  <Button
                    onClick={() => updateApplicationStatus(selectedApp.id, 'intro_scheduled', adminNotes)}
                    disabled={updating}
                    className="btn-accent text-sm"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Intro Scheduled
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => updateApplicationStatus(selectedApp.id, 'not_selected', adminNotes)}
                    disabled={updating}
                    className="text-sm text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Not Selected
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminApplications;
